import React, { useEffect, useMemo, useRef, useState } from "react";

import { useNavigate, useParams } from "react-router-dom";

import { Clock, ExternalLink } from "lucide-react";

import {

  getLiveInterviewInvite,

  getLiveInterviewInviteJoinRequest,

} from "../api/liveInterviewApi";

import {

  isGenericParticipantLabel,

  isValidGuestDisplayName,

  normalizeGuestDisplayName,

  resolveInviteCandidateDisplay,

} from "../utils/liveInterviewDisplayUtils";

import "../LiveInterview.css";



const normalizeJoinRequestStatus = (raw) => {

  const s = String(raw || "").trim().toLowerCase();

  if (!s) return "pending";

  if (["approved", "accepted", "admitted", "allowed"].includes(s)) return "approved";

  if (["rejected", "reject", "declined", "decline", "denied", "deny"].includes(s)) return "rejected";

  if (["removed", "remove"].includes(s)) return "removed";

  if (s === "joined") return "joined";

  if (s === "pending") return "pending";

  return s;

};



const JoinRoomPage = () => {

  const { inviteToken } = useParams();

  const navigate = useNavigate();

  const [payload, setPayload] = useState(null);

  const [loading, setLoading] = useState(true);

  const [error, setError] = useState("");

  const [joinRequest, setJoinRequest] = useState(null);

  const [requesting, setRequesting] = useState(false);

  const [statusError, setStatusError] = useState("");

  const [recordingConsentAccepted, setRecordingConsentAccepted] = useState(false);

  const [guestName, setGuestName] = useState("");



  const loadInvite = async () => {

    setLoading(true);

    setError("");

    try {

      const data = await getLiveInterviewInvite(inviteToken);

      setPayload(data);

    } catch (err) {

      setError(err.message || "Invite link could not be loaded.");

    } finally {

      setLoading(false);

    }

  };



  useEffect(() => {

    loadInvite();

    // eslint-disable-next-line react-hooks/exhaustive-deps

  }, [inviteToken]);



  const room = payload?.room;

  const invite = payload?.invite;

  const roomSchedulingMode = String(room?.scheduling_mode || "").trim().toLowerCase();

  const isInstantMeeting = roomSchedulingMode === "instant";

  const inviteAllowsDirectJoin =

    String(invite?.join_mode || "").trim().toLowerCase() === "direct" ||

    isInstantMeeting;

  const joinStatus = joinRequest?.status || "";

  const normalizedJoinStatus = joinRequest ? normalizeJoinRequestStatus(joinStatus) : "idle";



  const inviteDisplay = useMemo(

    () =>

      resolveInviteCandidateDisplay({

        candidateName: invite?.participant_name || room?.candidate_name,

        candidateEmail: invite?.participant_email || room?.candidate_email,

        roomTitle: room?.title,

        jobTitle: room?.job_title,

      }),

    [invite, room]

  );



  const presetInviteName = useMemo(() => {

    const raw = inviteDisplay.fullName || invite?.participant_name || "";

    return isGenericParticipantLabel(raw) ? "" : normalizeGuestDisplayName(raw);

  }, [inviteDisplay.fullName, invite?.participant_name]);



  const effectiveJoinName = isInstantMeeting

    ? normalizeGuestDisplayName(guestName)

    : presetInviteName || inviteDisplay.fullName || invite?.participant_name || "Candidate";



  const guestNameValid = !isInstantMeeting || isValidGuestDisplayName(effectiveJoinName);



  const inviteSubtitle = useMemo(() => {

    if (isInstantMeeting) {

      return "Enter your name to open the meeting preview. You will join the call when you click Join — no host approval needed.";

    }

    if (inviteAllowsDirectJoin) {

      return "Anyone with this link can join after accepting the notice below — no host approval needed.";

    }

    return "Continue to the meeting preview to check your camera, microphone, and speakers. You will request to join after that.";

  }, [inviteAllowsDirectJoin, isInstantMeeting]);



  const goToRoom = (request = null) => {

    if (!room?.room_id) return;

    const params = new URLSearchParams({

      inviteToken: inviteToken || "",

      name: effectiveJoinName,

      consent: recordingConsentAccepted ? "1" : "0",

    });

    const bookingId = String(room?.booking_id || invite?.booking_id || "").trim();

    if (bookingId) {

      params.set("booking_id", bookingId);

    }

    const requestId = String(request?.request_id || "").trim();

    if (requestId) {

      params.set("joinRequestId", requestId);

    }

    navigate(`/interview-room/${room.room_id}?${params.toString()}`);

  };



  const handleInstantContinue = async () => {

    if (!inviteToken || !room?.room_id) return;

    if (!guestNameValid) {

      setStatusError("Please enter your name (at least 2 characters) before continuing.");

      return;

    }

    setStatusError("");

    const params = new URLSearchParams({

      inviteToken: inviteToken || "",

      name: effectiveJoinName,

      consent: recordingConsentAccepted ? "1" : "0",

    });

    const bookingId = String(room?.booking_id || invite?.booking_id || "").trim();

    if (bookingId) {

      params.set("booking_id", bookingId);

    }

    navigate(`/interview-room/${room.room_id}?${params.toString()}`);

  };



  const handleAskToJoin = async () => {

    if (!inviteToken || !room?.room_id) return;

    if (isInstantMeeting) {

      await handleInstantContinue();

      return;

    }

    goToRoom();

  };


  const previewRedirectedRef = useRef(false);

  useEffect(() => {

    if (previewRedirectedRef.current) return;

    if (loading || error || !room?.room_id || isInstantMeeting) return;

    previewRedirectedRef.current = true;

    setRequesting(true);

    goToRoom();

  }, [loading, error, room?.room_id, isInstantMeeting, inviteToken, effectiveJoinName]);



  useEffect(() => {

    if (

      inviteAllowsDirectJoin ||

      !joinRequest?.request_id ||

      normalizeJoinRequestStatus(joinRequest.status) !== "pending"

    ) {

      return undefined;

    }



    let cancelled = false;

    let intervalId = null;

    const poll = async () => {

      if (cancelled) return;

      try {

        const data = await getLiveInterviewInviteJoinRequest(inviteToken, joinRequest.request_id);

        if (cancelled) return;

        setJoinRequest(data.request);

        if (normalizeJoinRequestStatus(data.request?.status) === "approved") {

          goToRoom(data.request);

          if (intervalId != null) {

            window.clearInterval(intervalId);

            intervalId = null;

          }

          cancelled = true;

        }

      } catch (err) {

        if (cancelled) return;

        console.error("[LiveInterview] Failed to poll join request", err);

        setStatusError(err.message || "Could not check join request status.");

      }

    };



    void poll();

    intervalId = window.setInterval(() => {

      void poll();

    }, 1200);



    return () => {

      cancelled = true;

      if (intervalId != null) {

        window.clearInterval(intervalId);

      }

    };

    // eslint-disable-next-line react-hooks/exhaustive-deps

  }, [inviteAllowsDirectJoin, inviteToken, joinRequest?.request_id]);



  return (

    <div className="live-interview-page live-interview-invite-page">

      <main className="live-interview-shell">

        <section className="live-interview-panel live-interview-invite-card">

          {loading ? <h1 className="live-interview-title live-interview-invite-title">Loading invite...</h1> : null}

          {error ? <div className="live-interview-error">{error}</div> : null}

          {!loading && !error && room && !isInstantMeeting ? (
            <>
              <h1 className="live-interview-title live-interview-invite-title">Opening device preview...</h1>
              <p className="live-interview-subtitle live-interview-invite-subtitle">
                Check your camera, microphone, and speakers next. You will request to join when you are ready.
              </p>
            </>
          ) : null}

          {!loading && !error && room && isInstantMeeting ? (

            <>

              <p className="live-interview-invite-greeting">

                {isInstantMeeting

                  ? "You're joining an instant interview"

                  : inviteDisplay.firstName

                    ? `Hi, ${inviteDisplay.firstName}`

                    : "You're invited to an interview"}

              </p>

              <h1 className="live-interview-title live-interview-invite-title">{inviteDisplay.jobHeading}</h1>

              <p className="live-interview-subtitle live-interview-invite-subtitle">{inviteSubtitle}</p>



              {isInstantMeeting ? (

                <div className="live-interview-field live-interview-invite-name-field">

                  <label htmlFor="instant-guest-name">Your name</label>

                  <input

                    id="instant-guest-name"

                    type="text"

                    className="live-interview-input"

                    placeholder="Enter your full name"

                    value={guestName}

                    onChange={(event) => {

                      setGuestName(event.target.value);

                      setStatusError("");

                    }}

                    autoComplete="name"

                    maxLength={80}

                  />

                  <p className="live-interview-join-muted">

                    This name is shown to the interviewer in the meeting.

                  </p>

                </div>

              ) : null}



              {!inviteAllowsDirectJoin && normalizedJoinStatus === "pending" ? (

                <div className="live-interview-waiting-card">

                  <Clock size={18} />

                  Waiting for the host to let you in. We will open the room automatically after approval.

                </div>

              ) : null}



              {!inviteAllowsDirectJoin && normalizedJoinStatus === "approved" && joinRequest?.request_id ? (

                <div className="live-interview-notice live-interview-invite-notice">

                  You are approved. Opening the interview room now.

                  <div className="live-interview-invite-inline-action">

                    <button

                      type="button"

                      className="live-interview-btn primary"

                      onClick={() => goToRoom(joinRequest)}

                    >

                      <ExternalLink size={16} />

                      Continue to interview room

                    </button>

                  </div>

                </div>

              ) : null}



              {!inviteAllowsDirectJoin && normalizedJoinStatus === "rejected" ? (

                <div className="live-interview-error">The host rejected this request. You can ask to join again.</div>

              ) : null}



              {inviteAllowsDirectJoin && !isInstantMeeting ? (

                <div className="live-interview-notice live-interview-invite-notice">

                  This link lets you enter the room directly after you accept the notice below.

                </div>

              ) : null}



              {statusError ? <div className="live-interview-error">{statusError}</div> : null}



              {!isInstantMeeting ? (

                <label className="live-interview-consent-card live-interview-invite-consent">

                  <input

                    type="checkbox"

                    checked={recordingConsentAccepted}

                    disabled={

                      !inviteAllowsDirectJoin &&

                      (normalizedJoinStatus === "pending" || normalizedJoinStatus === "approved")

                    }

                    onChange={(event) => {

                      setRecordingConsentAccepted(event.target.checked);

                      if (event.target.checked) {

                        setStatusError("");

                      }

                    }}

                  />

                  <span>

                    I understand this interview may be recorded and transcribed when those features are turned on. I agree

                    to join with this notice.

                  </span>

                </label>

              ) : null}



              <div className="live-interview-invite-actions">

                <button

                  type="button"

                  className="live-interview-btn primary live-interview-invite-primary"

                  onClick={handleAskToJoin}

                  disabled={

                    requesting ||

                    (!inviteAllowsDirectJoin && normalizedJoinStatus === "pending") ||

                    (!inviteAllowsDirectJoin && normalizedJoinStatus === "approved") ||

                    (!isInstantMeeting && !recordingConsentAccepted) ||

                    (isInstantMeeting && !guestNameValid)

                  }

                >

                  <ExternalLink size={16} />

                  {isInstantMeeting

                    ? requesting

                      ? "Opening preview..."

                      : "Continue to meeting preview"

                    : inviteAllowsDirectJoin

                      ? requesting

                        ? "Opening preview..."

                        : "Continue to device preview"

                      : requesting

                        ? "Asking..."

                        : normalizedJoinStatus === "pending"

                          ? "Waiting for host"

                          : "Continue to device preview"}

                </button>

              </div>

            </>

          ) : null}

        </section>

      </main>

    </div>

  );

};



export default JoinRoomPage;

